import Head from 'next/head'
import { NextSeo } from 'next-seo'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { BlogCategoryHeader } from 'src/components/BlogCategoryHeader/BlogCategoryHeader'
import { PostCards } from 'src/components/PostCards/PostCards'
import { config } from 'src/config'
import { fetchBlogCategories } from 'src/data/providers/fetchBlogCategories'
import { fetchBlogCategoryPosts } from 'src/data/providers/fetchBlogCategoryPosts'
import { getCategoryPageCanonicals } from 'src/functions/i18n/getCategoryPageCanonicals'
import { getLanguageFromLocale, getLocaleFromLanguage } from 'src/functions/i18n/localeFns'
import { pushBlogPageImpression } from 'src/functions/util/dataLayer'
import { useCartQuery } from 'src/hooks/useCartQuery'

import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Language } from 'src/enum/Language'
import type { Locale } from 'src/enum/Locale'
import type { Category } from 'src/types/Category'
import type { Post } from 'src/types/Post'

interface CategoryIndexProps {
  posts: Post[]
  totalPages: number
  currentCategoryId?: string
  currentCategorySlug?: string
  blogCategories: Category[]
  canonicals: {
    canonical: string
    xDefault?: string
    alternate: {
      locale: Locale
      relUrl: string
      hrefLang: string
    }[]
  }
}

export const CategoryIndex = ({
  posts,
  totalPages,
  currentCategoryId,
  currentCategorySlug,
  blogCategories,
  canonicals,
}: CategoryIndexProps) => {
  const { t } = useTranslation()
  const {
    isFetched: cartIsFetched,
    computed: { isLoggedIn },
  } = useCartQuery()

  useEffect(() => {
    if (cartIsFetched) {
      pushBlogPageImpression(isLoggedIn)
    }
  }, [cartIsFetched])

  return (
    <>
      <NextSeo
        title={t('cms.blogCategories.title')}
        titleTemplate={config.nextSeo.titleTemplate}
        description={t('cms.blogCategories.description')}
        openGraph={{
          type: 'article',
          title: t('cms.blogCategories.welcomeText'),
          description: t('cms.blogCategories.description'),
          site_name: config.nextSeo.openGraph.site_name,
          images: config.nextSeo.openGraph.images,
        }}
      />
      <Head>
        <link rel="canonical" href={canonicals.canonical} />
        {canonicals.xDefault && <link rel="alternate" hrefLang="x-default" href={canonicals.xDefault} />}
        {canonicals.alternate.map((link) => (
          <link key={`${link.locale}.relUrl`} rel="alternate" href={link.relUrl} hrefLang={link.hrefLang} />
        ))}
      </Head>

      <BlogCategoryHeader categories={blogCategories} currentSlug={currentCategorySlug} />
      <PostCards posts={posts} totalPages={totalPages} categoryId={currentCategoryId} />
    </>
  )
}

const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!params) return { props: {} }
  const currentCategorySlug = params.category || null

  const lang = params.lang as Language
  const locale = getLocaleFromLanguage(lang)

  const findCategoryIdBySlug = (slug: string | string[] | null, categories: Category[], _lang: Language) => {
    const foundCategory = categories.find(
      (blogCategory) => blogCategory.slug === slug && blogCategory.lang && _lang.indexOf(blogCategory.lang) !== -1,
    )

    return foundCategory ? foundCategory.id : null
  }

  const blogCategories = await fetchBlogCategories()
  const currentCategoryId = findCategoryIdBySlug(currentCategorySlug, blogCategories, lang)
  const postsResponse = await fetchBlogCategoryPosts(locale, currentCategoryId)

  return {
    props: {
      blogCategories,
      canonicals: getCategoryPageCanonicals(blogCategories, locale, currentCategoryId),
      posts: postsResponse.data,
      currentCategorySlug,
      currentCategoryId,
      totalPages: parseInt(postsResponse.headers['x-wp-totalpages'], 10),
    },
  }
}

const getStaticPaths: GetStaticPaths = async () => {
  const paths: string[] = []

  // fetch category paths
  const allCategories = await fetchBlogCategories()

  config.locales.forEach((locale) => {
    const language = getLanguageFromLocale(locale as Locale)

    // keep only categories that match current language
    allCategories
      .filter((category) => category.lang.startsWith(language))
      .forEach((category) => {
        paths.push(`/${language}/a/${category.slug}`)
      })
  })

  return {
    paths,
    fallback: false,
  }
}

export { getStaticPaths, getStaticProps }

export default CategoryIndex
